import BrightnessFourIcon from '@material-ui/icons/Brightness4';
import AssignmentIcon from '@material-ui/icons/Assignment';
import GetAppIcon from '@material-ui/icons/GetApp';
import HistoryIcon from '@material-ui/icons/History';
import InsertChartIcon from '@material-ui/icons/InsertChartOutlined';
import LinearScaleIcon from '@material-ui/icons/LinearScale';
import PropTypes from 'prop-types';
import { Component, default as React } from 'react';
import { Redirect } from 'react-router-dom';
import { APPLICATION_PATHS, ENTITLEMENTS, EDITING_OPTIONS } from '../application-core/constants';
import ComponentBody from '../core/ComponentBody';
import ComponentDetail from '../core/ComponentDetail';
import HistoryViewer from '../core/HistoryViewer';
import HierarchyIcon from '../core/icons/Hierarchy';
import InAppBar from '../core/InAppBar';
import TripleViewer from '../core/TripleViewer';
import { getGraphFromProject } from '../core/utils/utils';
import withGS from '../core/withGShoc';
import { NS_GS } from '../grafsync/constants';
import OntologyExport from './OntologyExport';
import OntologyQuality from './OntologyQuality';
import OntologyStatistics from './OntologyStatistics';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import LifeCycle from '../application-core/Lifecycle';

class OntologyData extends Component {

  constructor(props) {
    super(props);
    this.state = {
      view: 0,
      toIri: null
    };
  }

  changeView = (index) => this.setState({ view: index });

  toggleOntologyDetail = () => this.props.toggleOntologyDetail(1);

  onDisable = (item, disable = true) => {
    const disabledFeatures = this.props.data.disabledFeatures;
    const name = "quality-" + item;
    if (disable && disabledFeatures && disabledFeatures.includes(name)) return;
    // Hmm, where is it!
    if (!disable && disabledFeatures && !disabledFeatures.includes(name)) return;
    if (disable) {
      this.props.onAdd('disabledFeatures', name);
    }
    else {
      this.props.onDelete('disabledFeatures', name);
    }
  };

  render() {

    const { id, data, projectIRI, entitlements } = this.props;
    const { view, toIri } = this.state;
    const graph = getGraphFromProject(projectIRI);
    const av = data.editingOption && data.editingOption.includes(EDITING_OPTIONS.ADVANCED_VIEW);
    let resourceTypeOptions = [
      {

        "value": "http://www.w3.org/2002/07/owl#Class",
        "label": "Class"
      },
      {
        "value": "http://www.w3.org/2002/07/owl#ObjectProperty",
        "label": "Object Property"
      },
      {
        "value": "http://www.w3.org/2002/07/owl#DatatypeProperty",
        "label": "Datatype Property"
      },
      {
        "value": "http://www.w3.org/2002/07/owl#AnnotationProperty",
        "label": "Annotation Property"
      }];
    if (av) {
      resourceTypeOptions.push({
        "value": "http://www.w3.org/2002/07/owl#NamedIndividual",
        "label": "Named Individual"
      });
      resourceTypeOptions.push({
        "value": "http://www.w3.org/2000/01/rdf-schema#Datatype",
        "label": "Datatype"
      });
    }
    if (toIri) {
      return <Redirect to={APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/detail#project=' + projectIRI} />;
    }

    const appBar = <InAppBar
      tabValue={view}
      onChangeTab={this.changeView}
      tabItems={[
        {
          icon: HierarchyIcon,
          label: "Detail"
        },
        {
          icon: LinearScaleIcon,
          label: "Triples"
        },
        {
          icon: HistoryIcon,
          label: "History"
        },
        {
          icon: InsertChartIcon,
          label: "Statistics"
        },
        {
          icon: AssignmentIcon,
          label: "Quality"
        },
        {
          icon: GetAppIcon,
          label: "Export"
        },
        {
          icon: BrightnessFourIcon,
          label: "Lifecycle"
        }
      ]} />;

    const maxWidth = [800, null, null, 800, null, 800][view];
    const ontologyLink = APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/ontology#project=' + projectIRI + "&ontology=" + id;

    return (
      <ProjectConsumer>
        {({ defaultLang, hasComponent, created, _created }) => (
          <ComponentBody maxWidth={maxWidth} title={data.title} id={data.id} onToggleView={this.toggleOntologyDetail} appBar={appBar}>
            {view === 0 && <ComponentDetail
              id={id}
              graph={graph}
              entitlements={entitlements}
              includeNotes={true}
              includeAnnotations={true}
              includeVersionIRI={true}
              includeDeprecatedExclusion={true}
              includeVersionInfo={true}
              includeTemplates={true}
              // Workflow only works where visibiilty is fully available
              includeWorkflow={_created || created > "2025-03-01T12:00:00.000Z" ? true : false}
              iriOptions={["propertiesInitialLowerCase"]}
              projectType="OntologyProject"
              batchupdateName="batchupdateOntologyProject"
              resourceTypeTitle="Resources"
              setToDraft="Ontology"
              entitlement={ENTITLEMENTS.EDIT_ONTOLOGY}
              canHideProps={
                [
                  {
                    label: "Comment",
                    property: "http://www.w3.org/2000/01/rdf-schema#comment"
                  },
                  {
                    label: "See Also",
                    property: "http://www.w3.org/2000/01/rdf-schema#seeAlso"
                  },
                  {
                    label: "Preferred Label",
                    property: "http://www.w3.org/2004/02/skos/core#prefLabel"
                  },
                  {
                    label: "Alternative Label",
                    property: "http://www.w3.org/2004/02/skos/core#altLabel"
                  },
                  {
                    label: "Definition",
                    property: "http://www.w3.org/2004/02/skos/core#definition"
                  },
                  {
                    label: "Example",
                    property: "http://www.w3.org/2004/02/skos/core#example"
                  },
                  {
                    label: "Note",
                    property: "http://www.w3.org/2004/02/skos/core#note"
                  },
                  {
                    label: "Editorial Note",
                    property: "http://www.w3.org/2004/02/skos/core#editorialNote"
                  },
                  {
                    label: "Scope Note",
                    property: "http://www.w3.org/2004/02/skos/core#scopeNote"
                  },
                  {
                    label: "History Note",
                    property: "http://www.w3.org/2004/02/skos/core#historyNote"
                  },
                  {
                    label: "Change Note",
                    property: "http://www.w3.org/2004/02/skos/core#changeNote"
                  }
                ]}
              onToggleView={this.toggleOntologyDetail}
              editingOptions={[
                EDITING_OPTIONS.ADVANCED_VIEW,
                EDITING_OPTIONS.OWL_DL,
                EDITING_OPTIONS.PUNNING
              ]}
              includeSlashHash={true} />}
            {view === 1 && <TripleViewer
              requiredEntitlement={ENTITLEMENTS.EDIT_ONTOLOGY}
              id={id}
              graph={graph}
              deletionOverrides={[
                "http://www.w3.org/2004/02/skos/core#broader",
                "http://www.w3.org/2004/02/skos/core#narrower",
                "http://www.w3.org/2004/02/skos/core#related",
                "http://www.w3.org/2002/07/owl#sameAs",
                "http://www.w3.org/2002/07/owl#differentFrom",
                "http://www.w3.org/2002/07/owl#equivalentProperty",
                "http://www.w3.org/2002/07/owl#equivalentClass"
              ]}
              displayTitle={false} />}
            {view === 2 && <HistoryViewer
              options={[
                {
                  label: "Ontology Only",
                },
                {
                  label: "All Resources Defined By Ontology",
                  resource: true,
                  path: [
                    "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
                    NS_GS + "visibility",
                    NS_GS + "invisibility"
                  ]
                }
              ]}
              id={id}
              context={data.id}
              onToggleView={this.toggleOntologyDetail}
              graph={graph}
              displayTitle={false} />}
            {view === 3 && <OntologyStatistics
              workflow={data.activateWorkflow === true}
              id={id}
              graph={graph} />}
            {view === 4 && <OntologyQuality
              id={id}
              data={data}
              link={ontologyLink}
              entitlement={ENTITLEMENTS.EDIT_ONTOLOGY}
              onDisable={this.onDisable}
              editingOptions={data.editingOption}
              defaultLang={defaultLang}
              graph={graph}
              onToggleView={this.toggleOntologyDetail}
            />}
            {view === 5 && <OntologyExport id={id} graph={graph} />}
            {view === 6 && <LifeCycle
              id={id}
              graph={graph}
              defaultLang={defaultLang}
              entitlements={entitlements}
              skosXL={false}
              projectType="OntologyProject"
              inProp={[
                "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
                "https://grafsync.graphifi.com/schema/gs/visibility"
              ]}
              labelType={["http://www.w3.org/2000/01/rdf-schema#label"]}
              sortProp="label"
              rto={resourceTypeOptions}
              useWorkflow={data.activateWorkflow === true}
              onToggleView={this.toggleOntologyDetail}
              components={hasComponent}
              projectIRI={projectIRI}
              componentIRI={id} />
            }
          </ComponentBody>
        )}
      </ProjectConsumer>
    );
  }

}

OntologyData.propTypes = {
  id: PropTypes.string.isRequired,
  entitlements: PropTypes.array,
  data: PropTypes.object.isRequired,
  projectIRI: PropTypes.string.isRequired,
  toggleOntologyDetail: PropTypes.func.isRequired
};

export default withGS(OntologyData);